import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PageLoader } from '../../components/common/Loader';
import Breadcrumb from '../../components/common/Breadcrumb';
import { useAuth } from '../../contexts/AuthContext';
import { useEmployees } from '../../hooks/useEmployees';
import {
    useLaborTimesheetById,
    useLaborTimesheetApprove,
    useLaborTimesheetHrApprove,
    useLaborTimesheetReject,
} from '../../hooks/useLaborTimesheets';
import { IconChevronLeft, IconCheck, IconX, IconAlertTriangle, IconAlertCircle } from '@tabler/icons-react';
import { months } from '../../data/timesheetData';

const statusColors = {
    draft:       'text-gray-600 bg-gray-100',
    submitted:   'text-blue-700 bg-blue-100',
    approved:    'text-indigo-700 bg-indigo-100',
    hr_approved: 'text-green-700 bg-green-100',
    rejected:    'text-red-700 bg-red-100',
};

const fmtLoe = (val) => {
    if (val === null || val === undefined) return '-';
    return (parseFloat(val) * 100).toFixed(1) + '%';
};

export default function LaborTimesheetApprovalDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;

    const isHR = user?.is_superuser || hasAnyPermission(['rbacapp.hr_act', 'rbacapp.hr_approve', 'rbacapp.hr_view']);

    const { data: sheet, isLoading } = useLaborTimesheetById(id);

    // The approve/reject mutations cache their result under the "current" (self-service)
    // key, not this detail view's key — refresh this query's cache manually so the
    // status/buttons update immediately without a page reload.
    const applyUpdatedSheet = (data) => {
        queryClient.setQueryData(['labor-timesheet', 'detail', id], data);
        queryClient.invalidateQueries({ queryKey: ['labor-timesheet', 'list'] });
    };
    const { data: employees } = useEmployees(orgId);

    const approveApi = useLaborTimesheetApprove();
    const hrApproveApi = useLaborTimesheetHrApprove();
    const rejectApi = useLaborTimesheetReject();

    const [rejectModal, setRejectModal] = useState({ open: false, reason: '' });

    if (isLoading || !sheet) return <PageLoader />;

    const employeeRecord = (employees || []).find((e) => e.id === sheet.employee);
    const employeeName = employeeRecord?.name || sheet.employee_name || 'Employee';

    const canApprove = !isHR && sheet.status === 'submitted';
    const canHrApprove = isHR && sheet.status === 'approved';
    const canReject = (!isHR && sheet.status === 'submitted') || (isHR && ['submitted', 'approved'].includes(sheet.status));

    const projectRows = sheet.project_rows || [];
    const visibleDays = (sheet.days || []).filter((d) => d.is_in_month && d.work_date);
    const loeSummary = sheet.loe_summary || [];
    const grandTotalHours = sheet.month_totals?.grand_total ?? 0;

    const statusLabel = (sheet.status || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

    const errorIssues = sheet.issues?.filter((i) => i.level === 'error') || [];

    const handleApprove = () => {
        approveApi.mutate({ id: sheet.id }, {
            onSuccess: (data) => { applyUpdatedSheet(data); toast.success('Timesheet approved.'); },
            onError: (err) => toast.error(err?.response?.data?.detail || 'Approval failed.'),
        });
    };

    const handleHrApprove = () => {
        hrApproveApi.mutate({ id: sheet.id }, {
            onSuccess: (data) => { applyUpdatedSheet(data); toast.success('HR approval recorded.'); },
            onError: (err) => toast.error(err?.response?.data?.detail || 'Approval failed.'),
        });
    };

    const handleReject = () => {
        if (!rejectModal.reason.trim()) { toast.error('Rejection reason is required.'); return; }
        rejectApi.mutate({ id: sheet.id, reason: rejectModal.reason.trim() }, {
            onSuccess: (data) => {
                applyUpdatedSheet(data);
                setRejectModal({ open: false, reason: '' });
                toast.success('Timesheet rejected and returned to staff.');
            },
            onError: (err) => toast.error(err?.response?.data?.detail || 'Rejection failed.'),
        });
    };

    return (
        <div className="flex flex-col min-h-screen gap-20 font-sans text-text-primary">
            <Breadcrumb title="Labour Timesheet Review" path="Approvals / Timesheets / Labour Distribution" />

            {/* Back button */}
            <button
                onClick={() => navigate('/approvals/timesheet')}
                className="btn btn-outline-secondary flex items-center gap-5 mb-15 self-start"
            >
                <IconChevronLeft size={16} />
                Back
            </button>

            {/* Staff & period */}
            <div className="bg-white rounded border border-border shadow-sm p-20">
                <div className="flex items-center justify-between mb-10">
                    <h2 className="text-lg font-bold text-gray-800">{employeeName}</h2>
                    <span className={`inline-flex items-center px-10 py-5 rounded-full text-xs font-bold uppercase ${statusColors[sheet.status] || statusColors.draft}`}>
                        {statusLabel}
                    </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-20 gap-y-14 pt-15 border-t border-border">
                    {[
                        { label: 'Staff ID', value: employeeRecord?.employee_no || '—' },
                        { label: 'Entity', value: sheet.entity || '—' },
                        { label: 'Report Period', value: `${months[(sheet.month || 1) - 1]} ${sheet.year}` },
                        { label: 'Total Hours', value: grandTotalHours.toFixed(2) },
                    ].map(({ label, value }) => (
                        <div key={label}>
                            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mb-3">{label}</p>
                            <p className="text-sm font-semibold text-gray-800 truncate">{value}</p>
                        </div>
                    ))}
                </div>
                {sheet.rejection_reason && (
                    <div className="mt-15 p-10 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                        <strong>Rejected:</strong> {sheet.rejection_reason}
                    </div>
                )}
            </div>

            {errorIssues.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-15">
                    <div className="flex items-center gap-8 text-red-700 font-semibold text-sm mb-8">
                        <IconAlertCircle size={18} /> Outstanding Issues
                    </div>
                    <ul className="text-xs text-red-600 space-y-4">
                        {errorIssues.map((issue, i) => <li key={i}>{issue.message}</li>)}
                    </ul>
                </div>
            )}

            {/* Project register */}
            <div className="bg-white rounded border border-border shadow-sm overflow-hidden">
                <div className="px-15 py-10 border-b border-border bg-org-blue-primary">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white">Project &amp; Engagement Register</p>
                </div>
                <div className="overflow-x-auto">
                    <table className="border-collapse text-xs w-full min-w-max">
                        <thead>
                            <tr className="bg-gray-50 border-b border-border">
                                <th className="px-10 py-8 text-left border-r border-border">Fund Code</th>
                                <th className="px-10 py-8 text-left border-r border-border min-w-[200px]">Project / Engagement</th>
                                <th className="px-10 py-8 text-center border-r border-border">Contracted LOE %</th>
                                <th className="px-10 py-8 text-center">Actual Hours</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projectRows.map((row, i) => {
                                const loeRow = loeSummary.find((l) => l.col_ref === row.col_ref);
                                return (
                                    <tr key={row.id} className={`border-b border-border ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                                        <td className="px-10 py-8 border-r border-border">{row.fund_code || '—'}</td>
                                        <td className="px-10 py-8 border-r border-border font-medium">{row.engagement_name || '—'}</td>
                                        <td className="px-10 py-8 text-center border-r border-border font-semibold">{fmtLoe(row.contracted_loe)}</td>
                                        <td className="px-10 py-8 text-center font-semibold">{(loeRow?.actual_hours ?? 0).toFixed(2)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Daily grid — read only */}
            <div className="bg-white rounded border border-border shadow-sm overflow-hidden">
                <div className="px-15 py-10 border-b border-border bg-org-blue-primary">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white">Daily Labour Distribution</p>
                </div>
                <div className="overflow-x-auto">
                    <table className="border-collapse text-xs min-w-max w-full">
                        <thead>
                            <tr className="bg-org-blue-primary text-white">
                                <th className="px-6 py-6 text-center border-r border-white/20">Day</th>
                                {projectRows.map((row) => (
                                    <th key={row.id} className="px-6 py-6 text-center border-r border-white/20 min-w-[70px]">{row.col_ref}</th>
                                ))}
                                <th className="px-6 py-6 text-center border-r border-white/20">Leave</th>
                                <th className="px-6 py-6 text-center">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleDays.map((day) => {
                                const rowTotal = projectRows.reduce((sum, row) => {
                                    const entry = sheet.entries?.find((e) => e.project_row === row.id && e.day_index === day.day_index);
                                    return sum + (entry ? parseFloat(entry.hours) || 0 : 0);
                                }, 0);
                                const rowBg = day.is_weekend ? 'bg-gray-100' : day.public_holiday ? 'bg-amber-50' : '';
                                return (
                                    <tr key={day.work_date} className={`border-b border-border ${rowBg}`}>
                                        <td className="px-8 py-4 border-r border-border font-medium">{day.day_index}</td>
                                        {projectRows.map((row) => {
                                            const entry = sheet.entries?.find((e) => e.project_row === row.id && e.day_index === day.day_index);
                                            return (
                                                <td key={row.id} className="px-6 py-4 text-center border-r border-border">
                                                    {entry ? parseFloat(entry.hours).toFixed(2) : '—'}
                                                </td>
                                            );
                                        })}
                                        <td className="px-6 py-4 text-center border-r border-border">{day.leave_code || '—'}</td>
                                        <td className="px-6 py-4 text-center font-bold text-org-blue-primary">{rowTotal > 0 ? rowTotal.toFixed(2) : '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {sheet.variance_explanation && (
                <div className="bg-white rounded border border-border shadow-sm p-15">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-6">Variance Explanation</p>
                    <p className="text-sm text-gray-700">{sheet.variance_explanation}</p>
                </div>
            )}

            {/* Approval actions */}
            <div className="bg-white rounded border border-border shadow-sm p-20">
                <div className="flex flex-wrap gap-8">
                    {canApprove && (
                        <button onClick={handleApprove} disabled={approveApi.isPending}
                            className="flex items-center gap-5 bg-indigo-600 text-white px-12 py-6 rounded text-xs font-semibold hover:opacity-90 disabled:opacity-50">
                            <IconCheck size={13} />
                            {approveApi.isPending ? 'Approving…' : 'Line Manager Approve'}
                        </button>
                    )}
                    {canHrApprove && (
                        <button onClick={handleHrApprove} disabled={hrApproveApi.isPending}
                            className="flex items-center gap-5 bg-green-600 text-white px-12 py-6 rounded text-xs font-semibold hover:opacity-90 disabled:opacity-50">
                            <IconCheck size={13} />
                            {hrApproveApi.isPending ? 'Approving…' : 'HR Manager Approve'}
                        </button>
                    )}
                    {canReject && (
                        <button onClick={() => setRejectModal({ open: true, reason: '' })}
                            className="flex items-center gap-5 bg-red-50 text-red-600 border border-red-200 px-12 py-6 rounded text-xs font-semibold hover:bg-red-100">
                            <IconX size={13} /> Reject
                        </button>
                    )}
                    {!canApprove && !canHrApprove && !canReject && (
                        <p className="text-xs text-gray-400 italic">No action available — this sheet is {statusLabel.toLowerCase()}.</p>
                    )}
                </div>
            </div>

            {rejectModal.open && (
                <>
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-20">
                        <div className="w-full max-w-[440px] bg-white rounded-xl shadow-2xl overflow-hidden">
                            <div className="bg-red-600 px-20 py-15 flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-white flex items-center gap-8">
                                    <IconAlertTriangle size={20} /> Reject Timesheet
                                </h3>
                                <button onClick={() => setRejectModal({ open: false, reason: '' })} className="p-4 hover:bg-white/20 rounded-full">
                                    <IconX size={20} className="text-white" />
                                </button>
                            </div>
                            <div className="p-20">
                                <label className="text-xs text-gray-500 font-semibold uppercase block mb-8">
                                    Rejection Reason <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    rows={4}
                                    value={rejectModal.reason}
                                    onChange={(e) => setRejectModal((s) => ({ ...s, reason: e.target.value }))}
                                    placeholder="Describe what needs to be corrected..."
                                    className="w-full border border-border rounded-lg p-12 text-sm resize-none focus:outline-none focus:border-red-400"
                                />
                            </div>
                            <div className="px-20 py-15 border-t border-border flex justify-end gap-10">
                                <button onClick={() => setRejectModal({ open: false, reason: '' })} className="btn btn-outline-secondary">
                                    Cancel
                                </button>
                                <button
                                    onClick={handleReject}
                                    disabled={rejectApi.isPending || !rejectModal.reason.trim()}
                                    className="bg-red-600 text-white px-16 py-8 rounded text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                                >
                                    {rejectApi.isPending ? 'Rejecting…' : 'Confirm Reject'}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div onClick={() => setRejectModal({ open: false, reason: '' })} className="fixed inset-0 z-[99] bg-black/50 backdrop-blur-sm" />
                </>
            )}
        </div>
    );
}
